import {IconButton} from '@wandb/components';
import * as React from 'react';
import {type CSSProperties} from 'react';
import {Icon, Popup} from 'semantic-ui-react';

import {type RunSignature} from '../../types/run';
import {DD_PRIVACY_HIDDEN} from '../../util/datadog/privacy';
import {toSorted} from '../../util/toSorted';
import {type Thumbnails} from '../MediaCard';
import {type ImageThumbnailSuccessMetadata} from '../PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {downloadSpritePart} from './downloadSpritePart';

function getSrcSet(thumbnails: Thumbnails, imageURL: string) {
  const thumbnailSrcSetString = toSorted(thumbnails, (a, b) => a.size - b.size)
    .map(thumbnail => `${thumbnail.directUrl} ${thumbnail.size}w`)
    .join(', ');

  // Append the original image url to the srcSet to be used for any size larger than the largest thumbnail
  return `${thumbnailSrcSetString}, ${imageURL} 9999w`;
}

const Image = ({
  width,
  step,
  imgRef,
  imageURL,
  imgStyle,
  trackSuccess,
  trackError,
  rolledBack,
  globalStep,
  filePath,
  originalWidth,
  originalPixelOffset,
  runSignature,
  boxesRef,
  masksRef,
  thumbnails,
  onImageLoad,
}: {
  width: number;
  step: number;
  imgRef: React.RefObject<HTMLImageElement | null>;
  imageURL: string;
  imgStyle: CSSProperties;
  trackSuccess: (metadata?: ImageThumbnailSuccessMetadata) => void;
  trackError: () => void;
  rolledBack: boolean;
  globalStep: number;
  filePath: string;
  originalWidth?: number | null;
  originalPixelOffset: number;
  runSignature: RunSignature;
  boxesRef?: React.RefObject<HTMLDivElement>;
  masksRef?: React.RefObject<HTMLDivElement>;
  thumbnails?: Thumbnails;
  onImageLoad?: (image: HTMLImageElement) => void;
}) => {
  const handleDownloadImage = React.useCallback(() => {
    if (!filePath || !originalWidth) {
      return;
    }

    downloadSpritePart(
      runSignature,
      filePath,
      originalPixelOffset,
      originalWidth,
      {
        boxesRef,
        masksRef,
      }
    );
  }, [
    boxesRef,
    filePath,
    masksRef,
    originalPixelOffset,
    originalWidth,
    runSignature,
  ]);

  return (
    <div>
      {thumbnails && thumbnails.length > 0 ? (
        <img
          data-dd-privacy={DD_PRIVACY_HIDDEN}
          data-test={`image-card-img-${step}`}
          ref={imgRef as React.RefObject<HTMLImageElement>}
          srcSet={getSrcSet(thumbnails, imageURL)}
          sizes={`${width}px`}
          src={imageURL}
          alt="card"
          style={imgStyle}
          onLoad={e => {
            const usedThumbnailSizeStr =
              e.currentTarget?.currentSrc?.match(/thumbnail_(\d+)/)?.[1];
            const usedThumbnailSize = usedThumbnailSizeStr
              ? parseInt(usedThumbnailSizeStr)
              : undefined;
            trackSuccess({
              hasThumbnails: thumbnails.length > 0,
              usedThumbnailSize,
            });
            onImageLoad?.(e.currentTarget);
          }}
          onError={trackError}
        />
      ) : (
        <img
          data-dd-privacy={DD_PRIVACY_HIDDEN}
          data-test={`image-card-img-${step}`}
          ref={imgRef as React.RefObject<HTMLImageElement>}
          src={imageURL}
          alt="card"
          style={imgStyle}
          onLoad={e => {
            trackSuccess({
              hasThumbnails: thumbnails != null && thumbnails.length > 0,
              usedThumbnailSize: undefined,
            });
            onImageLoad?.(e.currentTarget);
          }}
          onError={trackError}
        />
      )}
      {rolledBack && (
        <div className={'content-card__fallback'}>
          <Popup
            trigger={
              <Icon
                style={{color: 'white'}}
                size="small"
                name="exclamation circle"
              />
            }>
            No media was found for step: {globalStep}
            <br />
            The most recent step: {step} is being displayed
          </Popup>
        </div>
      )}

      {filePath &&
        originalWidth && (
          // oxlint-disable-next-line tailwindcss/no-restricted-classes -- grandfathered :has() usage
          <div className="content-card__download !h-24 !w-24 !bg-wb-surface/70 !p-0 [&:has(:focus-visible)]:opacity-100">
            <IconButton
              aria-label="Download image"
              icon="download"
              onPress={handleDownloadImage}
              size="sm"
              variant="ghost"
            />
          </div>
        )}
    </div>
  );
};

export default Image;
